import React from 'react';
import { RefreshCw, Search, Settings } from 'lucide-react';
import { Avatar } from '../../ui/common';
import { APP_VERSION_INFO, formatFullBuildInfo } from '../../config/version';
import { openSettingsSection } from '../settings/settingsDeepLink';
import type { ReleaseUpdateState } from '../../hooks/useReleaseUpdate';

export interface SidebarFooterProps {
  showSidebarLabels: boolean;
  isSettingsOpen: boolean;
  authLabel: string;
  avatarInitials: string;
  photoURL?: string | null;
  onOpenSettings: () => void;
  onOpenSearch: () => void;
  onCloseDrawer: () => void;
  releaseUpdate?: ReleaseUpdateState;
}

function FooterIconButton({
  testId,
  label,
  onClick,
  active = false,
  children,
}: {
  testId?: string;
  label: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        width: 28,
        height: 28,
        flex: '0 0 28px',
        borderRadius: 8,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? 'var(--z-fill-2)' : 'transparent',
        border: 'none',
        color: active ? 'var(--z-accent)' : 'var(--z-muted)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export const SidebarFooter: React.FC<SidebarFooterProps> = ({
  showSidebarLabels,
  isSettingsOpen,
  authLabel,
  avatarInitials,
  photoURL,
  onOpenSettings,
  onOpenSearch,
  onCloseDrawer,
  releaseUpdate,
}) => {
  const availableRelease = releaseUpdate?.availableRelease;

  return (
    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          width: '100%',
          minWidth: 0,
          justifyContent: showSidebarLabels ? 'flex-start' : 'center',
          padding: '14px 4px 0',
          borderTop: '1px solid var(--z-border)',
        }}
      >
        <button
          type="button"
          data-testid="open-settings-avatar"
          onClick={onOpenSettings}
          title="Abrir configuración"
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            borderRadius: '50%',
          }}
        >
          <Avatar
            src={photoURL ?? undefined}
            initials={avatarInitials}
            color="var(--z-accent)"
            size={36}
          />
        </button>
        {showSidebarLabels ? (
          <button
            type="button"
            data-testid="open-settings"
            onClick={onOpenSettings}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              textAlign: 'left',
              fontSize: 12.5,
              fontWeight: 600,
              lineHeight: 1.2,
              color: isSettingsOpen ? 'var(--z-accent-ink)' : 'var(--z-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
              flex: '1 1 auto',
              minWidth: 0,
            }}
            title="Abrir configuración"
          >
            {authLabel}
          </button>
        ) : null}
      </div>

      <div
        data-testid="app-version-badge"
        title={formatFullBuildInfo()}
        style={{
          display: 'flex',
          alignItems: 'center',
          // space-between stretched a single big gap across the whole row
          // instead of a normal gap between the version text and the icon
          // buttons — flex-start + auto-margin on the icon cluster keeps a
          // consistent gap regardless of how wide the row is.
          justifyContent: 'flex-start',
          gap: 6,
          minHeight: 42,
          padding: showSidebarLabels ? '7px 8px' : '7px 4px',
          border: '1px solid var(--z-border)',
          borderRadius: 12,
          background: 'var(--z-fill)',
          color: 'var(--z-text-2)',
          fontSize: 11,
          letterSpacing: '0.01em',
          userSelect: 'none',
        }}
      >
        <button
          type="button"
          data-testid="open-changelog"
          onClick={() => openSettingsSection('changelog')}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            minWidth: 0,
            whiteSpace: 'nowrap',
            border: 'none',
            padding: 0,
            background: 'transparent',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          <span style={{ color: 'var(--z-muted)', fontWeight: 600 }}>
            {APP_VERSION_INFO.formatted}
          </span>
          {showSidebarLabels && !availableRelease ? (
            <span
              data-testid="release-current-status"
              style={{ color: 'var(--z-muted)', whiteSpace: 'nowrap' }}
            >
              Actualizado
            </span>
          ) : null}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          {availableRelease ? (
            <button
              type="button"
              data-testid="release-update-badge"
              onClick={() => releaseUpdate?.reload()}
              title={`Nueva versión disponible: v${availableRelease.version}`}
              aria-label={`Actualizar a la versión ${availableRelease.version}`}
              style={{
                width: 28,
                height: 28,
                flex: '0 0 28px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: 8,
                background: 'var(--z-accent-soft)',
                color: 'var(--z-accent-ink)',
                cursor: 'pointer',
              }}
            >
              <RefreshCw size={14} strokeWidth={2.3} aria-hidden="true" />
            </button>
          ) : null}
          <FooterIconButton
            testId="nav-settings"
            label="Configuración"
            onClick={onOpenSettings}
            active={isSettingsOpen}
          >
            <Settings size={18} strokeWidth={1.8} aria-hidden="true" />
          </FooterIconButton>
          {showSidebarLabels ? (
            <FooterIconButton
              label="Buscar (Cmd+K)"
              onClick={() => {
                onOpenSearch();
                onCloseDrawer();
              }}
            >
              <Search size={18} strokeWidth={1.8} aria-hidden="true" />
            </FooterIconButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};
